// src/utils/loanStatusUtils.ts
import {
  BankingSyndicate,
  Loan,
  LoanKey,
  LoanKeys,
  LoanManagementRule,
  LoanType,
  RetrievedLoanInformation,
  Vessel,
} from "@/contexts/DealContext";

export type LoanStatus = {
  isDefined: boolean;
  status: "Searching" | "Found" | "Information needed" | "Validated";
};

export const getLoanStatus = (loanValue: unknown): LoanStatus => {
  let isDefined = false;
  let status: LoanStatus["status"] = "Searching";
  // console.log("loanValue", loanValue);
  if (loanValue && typeof loanValue === "object") {
    if ("value" in loanValue || "question" in loanValue) {
      // Cas où loanValue est un RetrievedLoanInformation
      const retrievedInfo = loanValue as RetrievedLoanInformation;
      isDefined = retrievedInfo.value !== undefined || retrievedInfo.question !== undefined;
      status = retrievedInfo.question ? "Information needed" : "Found";

      // Si l'info a déjà été validée, on change le statut
      if (retrievedInfo.validated) {
        status = "Validated";
      }
    } else {
      // Cas où loanValue est un objet (ex: vessel) contenant plusieurs RetrievedLoanInformation
      const values = Object.values(loanValue) as RetrievedLoanInformation[];
      isDefined = values.some((item) => item?.value !== undefined || item?.question !== undefined);

      if (values.some((item) => item?.validated)) {
        status = "Validated";
      } else if (values.some((item) => item?.question)) {
        status = "Information needed";
      } else if (values.some((item) => item?.value !== undefined)) {
        status = "Found";
      }
    }
  }

  return { isDefined, status };
};
const parentLoanKeys = [
  "amount",
  "borrower",
  "agent",
  "guarantors",
  "margin",
  "maturity_date",
  "banking_syndicate",
  "vessels",
  "contacts",
];
const trancheLoanKeys = [
  "amount",
  "borrower",
  "agent",
  "guarantors",
  "margin",
  "maturity_date",
  "banking_syndicate",
  "vessels",
];
export const getLoanKeysToDisplay = (loan: Loan): LoanKey[] => {
  const keys = LoanKeys.filter((key) => {
    if (loan.tranches?.length && loan.tranches?.length > 0 && !parentLoanKeys.includes(key)) {
      return false;
    }
    if (!loan.financed_boat_number || loan.financed_boat_number === 0) {
      return key !== "vessels";
    }

    if (loan.parentId && !trancheLoanKeys.includes(key)) {
      return false;
    }
    return true;
  });
  return keys;
};

type Contact = {
  firstname?: string;
  lastname?: string;
  email: string;
  role: string;
  organization: string;
};

export type CreateLoanInput = {
  id: string;
  parentId?: string;
  type: LoanType;
  file?: File;
  name: string;
  description: string;
  amount: number;
  currencyId: string;
  maturityDate?: string;
  documentUrl: string;
  // endAvailabilityDate?: string;
  // baseRate?: BaseRate;
  // committed: boolean;
  borrowers: string[];
  guarantors: string[];
  // marginRate: MarginRate;
  // commitmentFees?: number;
  fixedRate?: number;
  contacts: Contact[];
  bankingSyndicate: BankingSyndicate[];
  assets: Vessel[];
  LoanManagementRules?: LoanManagementRule[];
  // commitmentUnit: FeeUnit;
};

const cleanNumberString = (value: string): number | undefined => {
  const cleanedValue = value.replace(",", ".").replace(/[^0-9.-]+/g, "");
  const parsedValue = parseFloat(cleanedValue);
  return isNaN(parsedValue) ? undefined : parsedValue;
};

const parseGuarantors = (loan: Loan) => {
  try {
    console.log("Parsing guarantors:", loan.guarantors?.value);
    if (!loan.guarantors?.value) {
      return [];
    }
    if (!JSON.parse(loan.guarantors?.value)) {
      return [];
    }
    if (Array.isArray(loan.guarantors?.value)) {
      return loan.guarantors.value as string[];
    }
    return (
      (Array.isArray(JSON.parse(loan.guarantors?.value)) && JSON.parse(loan.guarantors?.value)) ||
      []
    );
  } catch (e) {
    console.log("Error parsing guarantors:", e);
    return [];
  }
};

const parseVessels = (loan: Loan, dates: { loanId: string; date: string; key: string }[]): Vessel[] => {
  console.log("Parsing vessels for loan:", loan);
  try {
    if (!loan.vessels?.value || !loan.vessels?.validated) {
      return [];
    }
    console.log("Parsing vessels:", loan.vessels?.value);
    if (Array.isArray(loan.vessels?.value)) {
      const vessels = loan.vessels.value as Vessel[];
      if (vessels.length === 1) {
        const vesselDate = dates.find(d => d.loanId === loan.id && d.key === 'delivery_date')?.date;
        if (vesselDate) {
          vessels[0].deliveryDate = vesselDate;
        }
      }
      return vessels;
    }
    if (!JSON.parse(loan.vessels?.value)) {
      return [];
    }
    return (
      (Array.isArray(JSON.parse(loan.vessels?.value)) && JSON.parse(loan.vessels?.value)) || []
    );
  } catch (e) {
    console.log("Error parsing vessels:", e);
    return [];
  }
};

const parseMaturityDate = (maturityDate: string | undefined): Date | undefined => {
  if (!maturityDate) return undefined;
  const date = new Date(maturityDate);
  return isNaN(date.getTime()) ? undefined : date;
};

const parseContacts = (loan: Loan): Contact[] => {
  try {
    if (!loan.contacts?.value || !loan.contacts?.validated) {
      return [];
    }
    if (Array.isArray(loan.contacts?.value)) {
      return loan.contacts.value.map((contact: any) => ({
        name: contact.name || "",
        email: contact.email || "",
        role: contact.role || "",
        organization: contact.organization || "",
      }));
    }
    const parsedContacts = JSON.parse(loan.contacts.value);
    if (Array.isArray(parsedContacts)) {
      return parsedContacts.map((contact: any) => ({
        name: contact.name || "",
        email: contact.email || "",
        role: contact.role || "",
        organization: contact.organization || "",
      }));
    }
    return [];
  } catch (e) {
    console.log("Error parsing contacts:", e);
    return [];
  }
};

export const loanToLoanToSend = (loan: Loan, dates: { loanId: string; date: string; key: string }[]): CreateLoanInput => {
  let maturityDateRule = loan.LoanManagementRules?.find(
    (rule) => rule.field === "maturity_date"
  );
  if (loan.maturity_date?.value?.rule) {
    maturityDateRule = {
      rule: loan.maturity_date.value.rule,
      field: "maturity_date",
      triggers: loan.maturity_date.value.reference_date_key ? [loan.maturity_date.value.reference_date_key as string] : [],
    }
  }
  return {
    id: loan.id,
    parentId: loan.parentId,
    type: loan.type || LoanType.SENIOR_TERM_LOAN,
    name: loan.name,
    description: loan.description || "",
    documentUrl: loan.documentUrl,
    file: loan.file,
    amount: Math.floor(parseFloat(loan.amount?.amount.value) || 0),
    currencyId: loan.amount?.currency.value || undefined,
    maturityDate: (loan.maturity_date?.value && loan.maturity_date?.value?.maturity_date)
      ? parseMaturityDate(loan.maturity_date?.value?.maturity_date)?.toISOString()
      : undefined,
    borrowers: [loan.borrower?.value].filter(Boolean),
    guarantors: parseGuarantors(loan),
    fixedRate: loan.margin?.value ? cleanNumberString(loan.margin.value) : undefined,
    contacts: loan.contacts?.value ? parseContacts(loan) : [],
    bankingSyndicate: loan.banking_syndicate?.value ? loan.banking_syndicate.value : [],
    assets: parseVessels(loan, dates),
    LoanManagementRules: maturityDateRule ? [maturityDateRule] : [],
  };
};
